import React, { useRef, useState } from 'react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { toast } from 'react-hot-toast';
import { FaCloudUploadAlt, FaPaperclip, FaSpinner, FaTrashAlt } from 'react-icons/fa';
import { storage } from '../../../config/firebaseConfig';
import {
  SHIPMENT_ALLOWED_FILE_TYPES,
  SHIPMENT_MAX_FILE_SIZE,
  formatFileSize,
} from '../../../utils/shipments';
import styles from './CargoTracking.module.css';

/**
 * Kargo fişi / irsaliye / teslim tutanağı yükleme alanı. Dosyalar doğrudan
 * Storage'a yüklenir; `onUploadingChange` ile form kaydı yükleme bitene kadar
 * bekletilir.
 */
const CargoAttachmentsField = ({ attachments, onChange, onUploadingChange }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const setBusy = (value) => {
    setUploading(value);
    onUploadingChange(value);
  };

  const uploadFiles = async (fileList) => {
    const accepted = Array.from(fileList || []).filter((file) => {
      if (!SHIPMENT_ALLOWED_FILE_TYPES.includes(file.type)) {
        toast.error(`${file.name}: desteklenmeyen dosya türü`);
        return false;
      }
      if (file.size > SHIPMENT_MAX_FILE_SIZE) {
        toast.error(`${file.name}: dosya 10 MB sınırını aşıyor`);
        return false;
      }
      return true;
    });
    if (accepted.length === 0) return;

    setBusy(true);
    try {
      const uploaded = await Promise.all(
        accepted.map(async (file) => {
          const storagePath = `companyShipments/${Date.now()}_${file.name}`;
          const snapshot = await uploadBytes(ref(storage, storagePath), file);
          return {
            name: file.name,
            url: await getDownloadURL(snapshot.ref),
            type: file.type,
            size: file.size,
            storagePath,
            uploadedAt: new Date().toISOString(),
          };
        })
      );
      onChange([...attachments, ...uploaded]);
    } catch (error) {
      console.error('Kargo dosyası yüklenemedi:', error);
      toast.error('Dosya yüklenirken bir hata oluştu');
    } finally {
      setBusy(false);
    }
  };

  const removeAttachment = async (index) => {
    const attachment = attachments[index];
    try {
      if (attachment?.storagePath) await deleteObject(ref(storage, attachment.storagePath));
    } catch (error) {
      // Storage'dan silinemese bile kayıttan düşürülür
      console.error('Kargo dosyası silinemedi:', error);
    }
    onChange(attachments.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div
        className={`${styles.dropZone} ${dragActive ? styles.dropZoneActive : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          uploadFiles(e.dataTransfer.files);
        }}
      >
        {uploading ? <FaSpinner className={styles.spin} /> : <FaCloudUploadAlt />}
        <span>
          {uploading ? 'Yükleniyor...' : 'Kargo fişi, irsaliye veya tutanağı sürükleyin ya da tıklayın'}
        </span>
        <small>PDF, görsel, Word veya Excel · en fazla 10 MB</small>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept={SHIPMENT_ALLOWED_FILE_TYPES.join(',')}
          onChange={(e) => {
            uploadFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {attachments.length > 0 && (
        <ul className={styles.fileList}>
          {attachments.map((file, index) => (
            <li key={file.storagePath || `${file.name}-${index}`} className={styles.fileItem}>
              <FaPaperclip className={styles.fileIcon} />
              <a href={file.url} target="_blank" rel="noopener noreferrer" className={styles.fileName}>
                {file.name}
              </a>
              <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
              <button
                type="button"
                className={styles.squareButton}
                onClick={() => removeAttachment(index)}
                aria-label="Dosyayı sil"
              >
                <FaTrashAlt />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CargoAttachmentsField;
